<template>
  <div class="most-highlighted-locations">
    <h2 class="global-h4 most-highlighted-locations__title">Most highlighted</h2>

    <p v-if="!props.rows.length" class="most-highlighted-locations__empty">No highlights yet</p>

    <div v-else class="most-highlighted-locations__list">
      <div v-for="row in props.rows" :key="row.locationId" class="most-highlighted-locations__row">
        <LocationAvatar :src="row.icon" :name="row.name" :size="34" />
        <div class="most-highlighted-locations__copy">
          <div class="most-highlighted-locations__name">{{ row.name }}</div>
          <div class="most-highlighted-locations__track">
            <div class="most-highlighted-locations__fill" :style="{ width: `${barPercent(row)}%` }"></div>
          </div>
        </div>
        <div class="most-highlighted-locations__count">{{ row.postCount }}</div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Components
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";

// Types
import type { LocationHighlightRow } from "@contracts/socialReports";

const props = defineProps<{
  rows: LocationHighlightRow[];
}>();

const maxPostCount = computed<number>(() => Math.max(0, ...props.rows.map(r => r.postCount)));

function barPercent(row: LocationHighlightRow): number {
  if (maxPostCount.value <= 0) return 0;
  return Math.max(3, Math.round((100 * row.postCount) / maxPostCount.value));
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Most highlighted locations =================================================
.most-highlighted-locations {
  height: 100%;
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__title {
    margin: 0 0 16px;
  }

  &__empty {
    margin: 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__list {
    gap: 14px;
    display: flex;
    flex-direction: column;
  }

  &__row {
    gap: 11px;
    display: flex;
    align-items: center;
  }

  &__copy {
    min-width: 0;
    flex-grow: 1;
  }

  &__name {
    margin-bottom: 5px;
    color: #102f41;
    font: 500 14px/17px sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__track {
    width: 100%;
    height: 6px;
    border-radius: 100px;
    overflow: hidden;
    background: #eef1f4;
  }

  &__fill {
    height: 100%;
    border-radius: 100px;
    background: #058587;
  }

  &__count {
    min-width: 22px;
    flex-shrink: 0;
    color: #102f41;
    font: 600 15px/18px $font-family;
    text-align: right;
  }
}
</style>
